import { T, Var } from 'gt-react';
import { CheckCircleIcon, XIcon } from '@phosphor-icons/react';
import {
  AlertContainer,
  AlertTitle,
  AlertDescription,
} from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { parseMoney, toMoneyNumber } from '@erp/shared-utils';
import { formatCurrency } from '@/components/common/Currency';

/**
 * The formatted balance a resolution actually cleared, or `null` when there was
 * nothing to clear (unknown, already settled, or a credit). Exported so the
 * "never print $0.00 as an achievement" rule is one tested decision.
 */
export function resolveClearedAmount(
  balanceBefore: string | null,
): string | null {
  if (balanceBefore == null) return null;
  // `parseMoney` throws on a malformed string. This runs during render of a
  // banner nobody asked for, so a bad value must degrade to the generic wording
  // rather than take the whole thread view down with it.
  try {
    if (toMoneyNumber(parseMoney(balanceBefore)) <= 0) return null;
  } catch {
    return null;
  }
  return formatCurrency(balanceBefore, { currency: 'USD' });
}

interface ThreadAutoResolvedBannerProps {
  /** Invoice number for the copy; falls back to generic wording when absent. */
  invoiceNumber: string | null;
  /** The balance the invoice carried before the refresh settled it. */
  balanceBefore: string | null;
  onDismiss: () => void;
}

/**
 * Explains a thread that resolved itself while the user was looking at it.
 *
 * The open-time Famous refresh can settle a thread mid-task — someone may have
 * been part-way through a collection email when the invoice came back paid.
 * Without this the only trace is a `status_change` row they'd have to scroll
 * the timeline to find, so the banner is persistent (no auto-dismiss) and
 * names the invoice and the amount that cleared.
 */
export function ThreadAutoResolvedBanner({
  invoiceNumber,
  balanceBefore,
  onDismiss,
}: ThreadAutoResolvedBannerProps) {
  // Only claim an amount "cleared" when there was genuinely something to clear.
  // Auto-resolve fires on `netAmount > 0 && balanceDue <= 0`, which says nothing
  // about the balance *before* the refresh — it can already be zero (the header
  // refresh closed it) or negative (a credit). Naming either as the amount
  // cleared would print "clearing the $0.00 balance" under a resolution banner.
  const clearedAmount = resolveClearedAmount(balanceBefore);

  return (
    <AlertContainer className="relative pr-10">
      <CheckCircleIcon />
      <AlertTitle>
        <T context="banner title, collection thread resolved itself after a Famous refresh">
          Thread resolved automatically
        </T>
      </AlertTitle>
      <AlertDescription>
        <p>
          {invoiceNumber ? (
            clearedAmount ? (
              <T context="banner body, named invoice with an outstanding balance came back paid in full from the Famous ERP">
                Invoice <Var>{invoiceNumber}</Var> came back paid in full from
                Famous, clearing the <Var>{clearedAmount}</Var> balance. This
                thread was resolved automatically.
              </T>
            ) : (
              <T context="banner body, named invoice came back paid in full from the Famous ERP">
                Invoice <Var>{invoiceNumber}</Var> came back paid in full from
                Famous. This thread was resolved automatically.
              </T>
            )
          ) : (
            <T context="banner body, the linked invoice came back paid in full from the Famous ERP">
              The linked invoice came back paid in full from Famous. This thread
              was resolved automatically.
            </T>
          )}
        </p>
      </AlertDescription>
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 size-6"
        onClick={onDismiss}
      >
        <XIcon className="size-4" />
        <span className="sr-only">
          <T context="accessible label for the button that dismisses the auto-resolved banner">
            Dismiss
          </T>
        </span>
      </Button>
    </AlertContainer>
  );
}
